import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { MailCheck, ArrowLeft } from "lucide-react";

export default function ForgotPassword({ portal = false }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const loginPath = portal ? "/portal/login" : "/login";

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (portal) await api.post("/portal/forgot-password", { identifier: email });
      else await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (ex) { setErr(formatApiError(ex)); }
    setBusy(false);
  };

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 ${portal ? "bg-[#F7F5FC]" : "bg-slate-50"}`}>
      <div className="w-full max-w-sm bg-white rounded-2xl border border-[#3B82F6]/10 shadow-sm p-8" data-testid="forgot-password-card">
        <div className="font-display font-semibold text-xl text-[#3B82F6]">Hi-Tech Silico</div>
        <div className={`text-[10px] font-semibold uppercase tracking-[0.3em] mb-6 ${portal ? "text-[#8B5CF6]" : "text-[#FC5F5F]"}`}>
          {portal ? "Client Portal" : "Manufacturing CRM"}
        </div>
        {sent ? (
          <div data-testid="forgot-success">
            <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
              <MailCheck size={22} strokeWidth={1.5} />
            </div>
            <h1 className="text-xl font-display tracking-tight text-[#3B82F6]">Check your inbox</h1>
            <p className="text-xs text-[#3B82F6]/60 mt-2 leading-relaxed">
              If <span className="font-medium">{email}</span> is registered, we've emailed a password reset link.
              It expires in 30 minutes.
            </p>
          </div>
        ) : (
          <>
            <h1 className="text-xl font-display tracking-tight text-[#3B82F6]">Forgot your password?</h1>
            <p className="text-xs text-[#3B82F6]/50 mt-1 mb-6">Enter your email and we'll send you a reset link.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#3B82F6]/60">Email</Label>
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                       className="mt-1.5" data-testid="forgot-email" />
              </div>
              {err && <p className="text-xs text-[#FC5F5F]" data-testid="forgot-error">{err}</p>}
              <Button type="submit" disabled={busy || !email} className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white h-11" data-testid="forgot-submit">
                {busy ? "Sending…" : "Send Reset Link"}
              </Button>
            </form>
          </>
        )}
        <Link to={loginPath} className="inline-flex items-center gap-1.5 text-xs text-[#3B82F6]/50 hover:text-[#3B82F6] mt-6 transition-colors duration-200" data-testid="forgot-back-link">
          <ArrowLeft size={13} /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
